import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { accessConfig } from "./config";

// Map country -> subdomain. Keep this in sync with the one in productgroup.js.
const COUNTRY_DOMAIN_MAP = {
  NZ: "nz.westendjewellery.com",
  UK: "uk.westendjewellery.com",
  US: "uk.westendjewellery.com",
  AU: "westendjewellery.com",
};
const DEFAULT_DOMAIN = "westendjewellery.com";

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const ProtectedRoute = ({ user, children }) => {
  const location = useLocation();

  // If user is not logged in on THIS domain's localStorage...
  if (!user) {
    // ...before assuming they're logged out everywhere, check the
    // wej_country cookie. Unlike localStorage (per-origin), that cookie is
    // set with domain=.westendjewellery.com at login, so it IS visible here
    // even when this specific subdomain's localStorage has nothing — e.g.
    // someone logged in on nz. clicking/scanning a link to the root domain.
    const cookieCountry = getCookie("wej_country")?.toUpperCase();
    if (cookieCountry) {
      const expectedDomain = COUNTRY_DOMAIN_MAP[cookieCountry] || DEFAULT_DOMAIN;
      if (expectedDomain !== window.location.hostname) {
        const currentPath = `${location.pathname}${location.search}`;
        window.location.href = `https://${expectedDomain}${currentPath}`;
        return null; // hold rendering while the browser navigates away
      }
      // Cookie says they belong on exactly this domain, but there's no local
      // user — their session here genuinely expired/was cleared. Fall
      // through to a real login below.
    }

    // Genuinely not logged in anywhere -> login, remembering where they
    // were headed so Login.js can send them straight back afterward.
    const redirectPath = `${location.pathname}${location.search}`;
    return <Navigate to={`/Login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Do NOT lowercase — keep original value ("Customization", "Both", etc.)
  const userType = (user.loginAccessType || "").trim();

  // Access rules for this userType
  const allowedRoutes = accessConfig[userType] || [];

  // FULL access
  if (allowedRoutes.includes("*")) {
    return children;
  }

  // Normalize page path
  const fullPath = location.pathname.toLowerCase();

  // Check if current route starts with an allowed route
  const isAllowed = allowedRoutes.some((route) =>
    fullPath.startsWith(route.toLowerCase())
  );

  // If not allowed → redirect
  if (!isAllowed) {
    return <Navigate to="/partnerdashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;